import React, { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const ContactSection = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      toast({
        title: "Message Sent!",
        description: "We'll get back to you as soon as possible.",
      });
      setFormData({ name: "", email: "", message: "" });
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div>
            <h2 className="text-sm font-semibold text-blue-600 uppercase tracking-wide mb-3">
              get in touch
            </h2>
            <h3 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              Curious how KJP Properties can help with your real estate portfolio?
            </h3>
            <p className="text-lg text-gray-600 mb-10">
              Drop us a line below and our team will reach out to discuss your needs.
            </p>

            {/* Contact Details */}
            <div className="space-y-6">
              <div className="flex items-start group">
                <div className="bg-blue-100 p-3 rounded-lg group-hover:bg-blue-600 transition-colors duration-300">
                  <Mail className="text-blue-600 group-hover:text-white transition-colors" size={24} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-semibold text-gray-500 uppercase mb-1">Email</p>
                  <a
                    href="mailto:info@kjppropertygroup.com"
                    className="text-lg text-gray-900 hover:text-blue-600 transition-colors"
                  >
                    info@kjppropertygroup.com
                  </a>
                </div>
              </div>

              <div className="flex items-start group">
                <div className="bg-blue-100 p-3 rounded-lg group-hover:bg-blue-600 transition-colors duration-300">
                  <Phone className="text-blue-600 group-hover:text-white transition-colors" size={24} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-semibold text-gray-500 uppercase mb-1">Phone</p>
                  <a
                    href="tel:+18324646257"
                    className="text-lg text-gray-900 hover:text-blue-600 transition-colors"
                  >
                    (832) 464-6257
                  </a>
                </div>
              </div>

              <div className="flex items-start group">
                <div className="bg-blue-100 p-3 rounded-lg group-hover:bg-blue-600 transition-colors duration-300">
                  <MapPin className="text-blue-600 group-hover:text-white transition-colors" size={24} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-semibold text-gray-500 uppercase mb-1">Address</p>
                  <p className="text-lg text-gray-900">
                    1014 GATECREST DR<br />
                    HOUSTON, TX 77032
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Name
                </label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your.email@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                  Message
                </label>
                <Textarea
                  id="message"
                  placeholder="Tell us about your real estate needs..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  rows={5}
                  className="w-full resize-none"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                size="lg"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold text-lg py-6 transition-all duration-300 hover:scale-[1.02] shadow-lg"
              >
                {isSubmitting ? (
                  "Sending..."
                ) : (
                  <>
                    <Send className="mr-2" size={20} />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;